import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import { Reveal, EASE } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { BRANDS_DATA, getBrand } from "@/constants/images";
import { BRAND_CONTENT } from "@/constants/brandContent";
import useSeo from "@/hooks/useSeo";

export default function BrandDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const brand = getBrand(id);
  const tr = brand ? t.brands.items[brand.id] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useSeo(
    brand ? `${brand.name} — Precision Workholding & Clamping | Teknik Enterprises` : "Principal | Teknik Enterprises",
    brand && tr ? `${tr.tagline}. ${tr.desc}`.slice(0, 300) : undefined,
    brand ? `https://www.workholdingworld.com/brands/${brand.id}` : undefined
  );

  if (!brand || !tr) {
    return (
      <div className="grid min-h-screen place-items-center bg-blueprint px-5 text-center">
        <div>
          <p className="font-display text-3xl uppercase text-white">Principal not found</p>
          <Link to="/" className="mt-4 inline-block text-steel hover:underline" data-testid="brand-notfound-home">
            ← Back to Teknik Enterprises
          </Link>
        </div>
      </div>
    );
  }

  const gallery = brand.gallery || [brand.image];
  const others = BRANDS_DATA.filter((b) => b.id !== brand.id);
  const content = BRAND_CONTENT[brand.id];

  return (
    <div className="min-h-screen bg-blueprint text-slate-200">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-ink/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-5 sm:px-8">
          <Link to="/" data-testid="brand-nav-logo">
            <Logo />
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="brand-back-link"
              className="hidden text-[13px] font-medium uppercase tracking-wide text-slate-300 transition-colors hover:text-white sm:inline"
            >
              ← All principals
            </Link>
            <a
              href="/#contact"
              data-testid="brand-cta-quote"
              className="inline-flex items-center gap-2 rounded-full bg-crimson px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-crimson-hover"
            >
              <i className="fa-solid fa-file-invoice" />
              Request a Quote
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-white/10">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 sm:px-8 lg:grid-cols-2 lg:gap-16 lg:py-24">
          <div>
            <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-steel">
              <span className="inline-block h-2 w-2 bg-crimson" />
              Principal · {tr.badge}
            </div>
            <h1
              className="mt-5 font-display text-4xl font-bold uppercase leading-[0.95] text-white sm:text-5xl lg:text-6xl"
              data-testid="brand-title"
            >
              {brand.name}
            </h1>
            <p className="mt-4 font-display text-xl font-semibold uppercase text-crimson sm:text-2xl">{tr.tagline}</p>
            <p className="mt-5 max-w-xl leading-relaxed text-slate-300">{tr.desc}</p>

            <div className="mt-8 flex flex-wrap gap-2.5">
              {Object.entries(brand.specs).map(([k, v]) => (
                <span
                  key={k}
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs text-slate-300"
                >
                  <span className="text-slate-500">{k}:</span>
                  <span className="font-medium text-steel">{v}</span>
                </span>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="clip-frame relative border border-white/10"
          >
            <img
              src={gallery[0]}
              alt={`${brand.name} workholding`}
              className="h-[360px] w-full object-cover lg:h-[460px]"
              data-testid="brand-hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
          </motion.div>
        </div>
      </section>

      {/* Product range + gallery */}
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
              {t.brands.productsLabel}
            </div>
            <ul className="mt-5 grid gap-3">
              {brand.products.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <li className="flex items-start gap-3 border-b border-white/5 pb-3 text-[15px] text-slate-200">
                    <i className="fa-solid fa-arrow-right mt-1.5 text-xs text-crimson" />
                    {p}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-2 gap-3" data-testid="brand-gallery">
            {gallery.map((src, i) => (
              <Reveal
                key={i}
                delay={i * 0.08}
                className={`clip-frame overflow-hidden border border-white/10 ${i === 0 ? "col-span-2" : ""}`}
              >
                <img
                  src={src}
                  alt={`${brand.name} detail ${i + 1}`}
                  loading="lazy"
                  className="h-56 w-full object-cover transition-transform duration-700 hover:scale-105 sm:h-64"
                  data-testid={`brand-gallery-img-${i}`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed overview — unique, in-depth content */}
      {content ? (
        <section className="border-t border-white/10">
          <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-20">
            <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:gap-16">
              <div>
                <h2 className="font-display text-2xl font-semibold uppercase text-white sm:text-3xl">
                  About {brand.name} workholding
                </h2>
                <div className="mt-5 space-y-4">
                  {content.overview.map((p, i) => (
                    <Reveal key={i} delay={i * 0.05}>
                      <p className="max-w-2xl leading-relaxed text-slate-300">{p}</p>
                    </Reveal>
                  ))}
                </div>

                <h3 className="mt-10 font-display text-xl font-semibold uppercase text-white">Key benefits</h3>
                <ul className="mt-4 grid gap-3">
                  {content.benefits.map((b, i) => (
                    <Reveal key={i} delay={i * 0.04}>
                      <li className="flex items-start gap-3 text-[15px] leading-snug text-slate-200">
                        <i className="fa-solid fa-circle-check mt-1 text-xs text-crimson" />
                        {b}
                      </li>
                    </Reveal>
                  ))}
                </ul>
              </div>

              <aside className="lg:pt-1">
                <div className="border border-white/10 bg-panel/40 p-6">
                  <h3 className="font-display text-lg font-semibold uppercase text-white">Typical applications</h3>
                  <ul className="mt-4 space-y-3">
                    {content.applications.map((a, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                        <i className="fa-solid fa-arrow-right mt-1 text-xs text-steel" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="mt-6 leading-relaxed text-slate-400">{content.closing}</p>
              </aside>
            </div>
          </div>
        </section>
      ) : null}

      {/* CTA */}
      <section className="border-y border-white/10 bg-navy/30">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 px-5 py-14 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="font-display text-2xl font-semibold uppercase text-white sm:text-3xl">
              Specifying {brand.name} for your shop?
            </h2>
            <p className="mt-2 max-w-xl text-sm text-slate-400">
              Share your component and machining setup — our application engineers will recommend the right {brand.name}{" "}
              configuration.
            </p>
          </div>
          <a
            href="/#contact"
            data-testid="brand-cta-bottom"
            className="inline-flex shrink-0 items-center gap-3 rounded-full bg-crimson px-7 py-3.5 text-sm font-semibold text-white transition-all hover:gap-4 hover:bg-crimson-hover"
          >
            Request a Quote
            <i className="fa-solid fa-arrow-right" />
          </a>
        </div>
      </section>

      {/* Other principals */}
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Other principals</div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {others.map((b) => (
            <Link
              key={b.id}
              to={`/brands/${b.id}`}
              data-testid={`brand-other-${b.id}`}
              className="group relative h-44 overflow-hidden border border-white/10"
            >
              <img
                src={b.image}
                alt={b.name}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
              <div className="absolute bottom-4 left-5">
                <div className="font-display text-xl font-semibold uppercase text-white">{b.name}</div>
                <div className="mt-1 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wide text-steel">
                  View details <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
